import {
  CircleStackIcon,
  GlobeAltIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { CloudIcon, InfoIcon } from 'lucide-react';
import React, { ReactNode } from 'react';

import { ConditionalRender } from '@/components/ConditionalRender';
import { Loader } from '@/components/Loader';
import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/ui';
import {
  MONITORING_NAME_MAP,
  MONITORING_TOOLTIP_MAP,
} from '@/hooks/monitoringConstants';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useGetSubscription } from '@/hooks/useSubscription';
import { MonitoringCategories, MonitoringCounts } from '@/types';
import { cn } from '@/utils/classname';

const MONITORING_CATEGORY_ICON_MAP: Record<MonitoringCategories, ReactNode> = {
  cloud: (
    <CloudIcon className="text-chariot-stroke size-6 shrink-0 [&_path]:stroke-[2px]" />
  ),
  externalNetwork: (
    <GlobeAltIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
  code: <CircleStackIcon className="text-chariot-stroke size-6 shrink-0" />,
  internalNetwork: (
    <LockClosedIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
  applications: (
    <GlobeAltIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
};

const MONITORING_CATEGORY_DESC_MAP: Record<MonitoringCategories, string> = {
  cloud: 'Total Number of Cloud Assets Monitored',
  internalNetwork: 'Total Number of Internal Network Assets Monitored',
  externalNetwork: 'Total Number of Perimeter Network Assets Monitored',
  code: 'Total Number of Repositories Monitored',
  applications: 'Total Number of Perimeter Applications Monitored',
};

function calculateCloudAssetTotal(cloud: MonitoringCounts['cloud']): number {
  return (
    cloud.amazon.external +
    cloud.azure.external +
    cloud.gcp.external +
    cloud.digitalocean.external
  );
}

function calculateCategoryTotal(
  category: Record<string, number | Record<string, number>>
): number {
  return Object.values(category).reduce((acc, curr) => {
    const currValue =
      typeof curr === 'number'
        ? curr
        : Object.values(curr).reduce((acc, curr) => acc + curr, 0);

    return (acc as number) + currValue;
  }, 0) as number;
}

/**
 * Monitoring component handles its own data fetching to work with Suspense pattern.
 * Fetches both subscription data (for limits) and monitoring counts internally.
 */
export function Monitoring(props: { email?: string; hideLimits?: boolean }) {
  // Fetch monitoring counts and subscription data
  const { data: monitoringCounts, status } = useGetMonitoringCounts({
    email: props.email,
  });
  const { data: subscriptionValue } = useGetSubscription({
    email: props.email,
  });

  // React 19: Simple reduce operations don't need useMemo (must be before early return for hooks rules)
  const totalAssets = !monitoringCounts
    ? 0
    : Object.keys(monitoringCounts)
        .filter(key => key !== 'applications')
        .reduce((acc, curr) => {
          const category = monitoringCounts[curr as MonitoringCategories];
          if (curr === 'cloud') {
            // Only count external cloud assets toward scannable total
            const cloud = category as MonitoringCounts['cloud'];
            return (
              acc +
              cloud.amazon.external +
              cloud.azure.external +
              cloud.gcp.external +
              cloud.digitalocean.external
            );
          }
          return acc + calculateCategoryTotal(category);
        }, 0);

  // React 19: Simple reduce operations don't need useMemo
  const totalApplications = !monitoringCounts?.applications
    ? 0
    : Object.values(monitoringCounts.applications).reduce(
        (acc, curr) => acc + curr,
        0
      );

  // Return null while loading to let parent Suspense handle loading state
  const isLoading = status === 'pending';
  if (isLoading || !monitoringCounts) {
    return null;
  }

  const { applications } = monitoringCounts;

  // Calculate current applications monitored (webseed count)
  const currentApplicationsMonitored = applications.webseed ?? 0;

  // Get limits from subscription data
  const maxAssetLimit = subscriptionValue?.numberOfAssets ?? 0;
  const maxApplicationLimit =
    subscriptionValue?.numberOfApplications ?? totalApplications ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {!props.hideLimits && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <UsageDisplay
            label="Scannable Asset Current Coverage vs Asset Contract Limit"
            current={totalAssets}
            limit={maxAssetLimit}
            showTooltip
            tooltipText="Number of assets currently being monitored compared to your subscription's contracted limit"
          />
          <UsageDisplay
            label="Application Current Coverage vs Application Contract Limit"
            current={currentApplicationsMonitored}
            limit={maxApplicationLimit}
            showTooltip
            tooltipText="Number of applications currently being monitored compared to your subscription's contracted limit"
          />
        </div>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 xl:grid-cols-3">
        {Object.keys(monitoringCounts).map((key, index) => {
          const categoryKey = key as MonitoringCategories;
          const category = monitoringCounts[categoryKey];

          const categoryItems = Object.entries(category);
          const categoryItemsExist = categoryItems.length > 0;

          return (
            <ConditionalRender
              key={index}
              condition={categoryKey === 'cloud'}
              conditionalWrapper={children => {
                return <div className="lg:row-span-2">{children}</div>;
              }}
            >
              <Container className="flex h-full w-full flex-col gap-3">
                <div className="mb-1 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold">
                      {MONITORING_NAME_MAP[categoryKey]}
                    </h3>
                  </div>
                  <Loader
                    className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
                    isLoading={isLoading}
                  >
                    <div className="border-chariot-stroke text-chariot-text-primary rounded-2xl border px-3 py-1 text-xs font-medium">
                      now
                    </div>
                  </Loader>
                </div>
                <div className="flex items-center gap-2">
                  {MONITORING_CATEGORY_ICON_MAP[categoryKey]}
                  <h4
                    className="text-sm font-bold"
                    style={{ whiteSpace: 'break-spaces' }}
                  >
                    {MONITORING_CATEGORY_DESC_MAP[categoryKey]}
                  </h4>
                  <Loader
                    className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
                    isLoading={isLoading}
                  >
                    <div className="bg-chariot-background-secondary ml-auto min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
                      {categoryKey === 'applications' && 'webseed' in category
                        ? (category.webseed as number).toString()
                        : categoryKey === 'cloud'
                          ? calculateCloudAssetTotal(
                              category as MonitoringCounts['cloud']
                            ).toString()
                          : calculateCategoryTotal(category).toString()}
                    </div>
                  </Loader>
                </div>
                {categoryItemsExist && (
                  <>
                    <div
                      className={cn('bg-chariot-stroke h-[2px] h-px w-full')}
                    />
                    <div className="flex flex-col gap-4">
                      {categoryItems.map(([key, value]) => {
                        const categoryKey = key;

                        return (
                          <div className="flex flex-col gap-2" key={key}>
                            <div className="flex w-full items-center justify-between">
                              <div className="flex items-center gap-2">
                                <h4
                                  className="text-sm font-bold capitalize"
                                  style={{ whiteSpace: 'break-spaces' }}
                                >
                                  {MONITORING_NAME_MAP[categoryKey] || key}
                                </h4>
                                <Tooltip
                                  title={
                                    MONITORING_TOOLTIP_MAP[categoryKey] || ''
                                  }
                                >
                                  <InfoIcon className="text-chariot-text-primary size-4" />
                                </Tooltip>
                              </div>
                              <Loader
                                className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
                                isLoading={isLoading}
                              >
                                <div className="bg-chariot-background-secondary min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
                                  {typeof value === 'number'
                                    ? value.toString()
                                    : calculateCategoryTotal(value).toString()}
                                </div>
                              </Loader>
                            </div>
                            {typeof value === 'object' && (
                              <div className="flex flex-col gap-1">
                                {Object.entries(value).map(([key, value]) => {
                                  return (
                                    <div
                                      key={key}
                                      className="flex w-full items-center justify-between pl-4"
                                    >
                                      <div className="flex items-center gap-2">
                                        <h4
                                          className="text-chariot-text-primary text-sm capitalize"
                                          style={{
                                            whiteSpace: 'break-spaces',
                                          }}
                                        >
                                          {MONITORING_NAME_MAP[key]}
                                        </h4>
                                      </div>
                                      <Loader
                                        className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
                                        isLoading={isLoading}
                                      >
                                        <div className="bg-chariot-background-secondary min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
                                          {(value as number).toString()}
                                        </div>
                                      </Loader>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </Container>
            </ConditionalRender>
          );
        })}
      </div>
    </div>
  );
}

interface UsageDisplayProps {
  label: string;
  current: number;
  limit: number;
  showTooltip?: boolean;
  tooltipText?: string;
}

const UsageDisplay: React.FC<UsageDisplayProps> = ({
  label,
  current,
  limit,
  showTooltip = false,
  tooltipText,
}) => {
  const percentage = Math.min((current / limit) * 100, 100);
  const isOverLimit = current > limit;

  return (
    <Container className="flex-1">
      <div className="mb-2 flex items-center gap-2">
        <div className="text-chariot-text-primary flex items-center gap-2 text-base font-medium">
          {label}
          {showTooltip && tooltipText && (
            <Tooltip title={tooltipText}>
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="mb-3 flex items-baseline gap-1">
        <span
          className={cn(
            'text-2xl font-bold',
            isOverLimit ? 'text-red-500' : 'text-chariot-brand-primary'
          )}
        >
          {current.toLocaleString()}
        </span>
        <span className="text-chariot-text-primary text-lg">/</span>
        <span className="text-chariot-text-primary text-lg">
          {limit.toLocaleString()}
        </span>
      </div>

      <div className="bg-chariot-background-secondary h-1 w-full overflow-hidden rounded-full">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            isOverLimit ? 'bg-red-500' : 'bg-chariot-brand-primary'
          )}
          style={{
            width: `${Math.min(percentage, 136)}%`, // Cap visual representation but allow overflow
          }}
        />
      </div>
    </Container>
  );
};
